import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import {
  ShieldCheck, Search, Eye, Loader2, Building2, MapPin, RefreshCw,
  CheckCircle2, AlertTriangle, ShieldAlert, Clock, FileX, Filter
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  brouillon: { label: "Brouillon", color: "bg-slate-100 text-slate-700", icon: Clock },
  incomplet: { label: "Incomplet", color: "bg-amber-100 text-amber-800", icon: AlertTriangle },
  a_completer: { label: "À compléter", color: "bg-orange-100 text-orange-800", icon: AlertTriangle },
  soumis: { label: "Soumis", color: "bg-blue-100 text-blue-800", icon: Eye },
  valide: { label: "Validé", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  valide_reserves: { label: "Validé avec réserves", color: "bg-yellow-100 text-yellow-800", icon: AlertTriangle },
  refuse: { label: "Refusé", color: "bg-red-100 text-red-800", icon: FileX },
  a_corriger: { label: "À corriger", color: "bg-orange-100 text-orange-800", icon: AlertTriangle },
};

const ScoreIndicator = ({ score }) => {
  const config = {
    conforme: { color: "bg-green-500", label: "OK" },
    vigilance: { color: "bg-amber-500", label: "!" },
    bloquant: { color: "bg-red-600", label: "X" },
  };
  const c = config[score] || config.conforme;
  return <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${c.color} text-white text-xs font-bold`}>{c.label}</span>;
};

export default function PrerequisTechniquesListPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [accessBlocked, setAccessBlocked] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [enseigneFilter, setEnseigneFilter] = useState('all');
  const [scoreFilter, setScoreFilter] = useState('all');
  const [createurFilter, setCreateurFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      // Check access first
      const accessRes = await fetch(`${API}/api/prerequis-techniques-access`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (accessRes.ok) {
        const accessData = await accessRes.json();
        if (accessData.is_blocked) { setAccessBlocked(true); setLoading(false); return; }
      }
      const res = await fetch(`${API}/api/prerequis-techniques`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setItems(await res.json());
    } catch { toast.error('Erreur réseau'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (token) fetchData(); }, [token]);

  const enseignes = useMemo(() => {
    const set = new Set(items.map(i => i.enseigne).filter(Boolean));
    return Array.from(set).sort();
  }, [items]);

  const createurs = useMemo(() => {
    const set = new Set(items.map(i => i.created_by_name).filter(Boolean));
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter(it => {
      const matchSearch = searchTerm === '' ||
        it.magasin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        it.ville?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        it.enseigne?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        it.code_postal?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        it.created_by_name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchStatus = statusFilter === 'all' || it.status === statusFilter;
      const matchEnseigne = enseigneFilter === 'all' || it.enseigne === enseigneFilter;
      const matchScore = scoreFilter === 'all' || it.score === scoreFilter;
      const matchCreateur = createurFilter === 'all' || it.created_by_name === createurFilter;
      let matchDate = true;
      if (dateFrom && it.created_at) matchDate = it.created_at >= dateFrom;
      if (dateTo && it.created_at && matchDate) matchDate = it.created_at <= dateTo + 'T23:59:59';
      return matchSearch && matchStatus && matchEnseigne && matchScore && matchCreateur && matchDate;
    });
  }, [items, searchTerm, statusFilter, enseigneFilter, scoreFilter, createurFilter, dateFrom, dateTo]);

  const stats = useMemo(() => {
    const s = { total: items.length, brouillon: 0, incomplet: 0, soumis: 0, valide: 0, refuse: 0 };
    items.forEach(it => {
      if (it.status === 'brouillon') s.brouillon++;
      else if (['incomplet', 'a_completer', 'a_corriger'].includes(it.status)) s.incomplet++;
      else if (it.status === 'soumis') s.soumis++;
      else if (['valide', 'valide_reserves'].includes(it.status)) s.valide++;
      else if (it.status === 'refuse') s.refuse++;
    });
    return s;
  }, [items]);

  const formatDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('fr-FR'); } catch { return '-'; }
  };

  if (loading) return <AppLayout><div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div></AppLayout>;
  if (accessBlocked) return (
    <AppLayout>
      <div className="text-center py-16">
        <ShieldAlert className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <p className="text-lg font-semibold text-slate-700">Accès restreint</p>
        <p className="text-sm text-slate-500 mt-1">Vous n'avez pas accès au module Prérequis Techniques.</p>
      </div>
    </AppLayout>
  );

  return (
    <AppLayout>
      <div className="space-y-5" data-testid="prerequis-list">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-amber-500" />
              Prérequis Techniques
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">Contrôle des contraintes techniques avant signature</p>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchData}><RefreshCw className="w-4 h-4" /></Button>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          {[
            { label: "Total", value: stats.total, color: "text-slate-700", bg: "bg-white" },
            { label: "Brouillons", value: stats.brouillon, color: "text-slate-500", bg: "bg-white" },
            { label: "Incomplets", value: stats.incomplet, color: "text-amber-600", bg: "bg-amber-50" },
            { label: "En validation", value: stats.soumis, color: "text-blue-600", bg: "bg-blue-50" },
            { label: "Validés", value: stats.valide, color: "text-green-600", bg: "bg-green-50" },
            { label: "Refusés", value: stats.refuse, color: "text-red-600", bg: "bg-red-50" },
          ].map(s => (
            <Card key={s.label} className={s.bg}>
              <CardContent className="p-2.5 text-center">
                <p className="text-xs text-slate-500 truncate">{s.label}</p>
                <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-3">
            <div className="flex flex-col gap-2">
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Rechercher magasin, ville, code postal, créateur..."
                    value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 h-9" data-testid="search-input" />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-[160px] h-9" data-testid="status-filter"><SelectValue placeholder="Statut" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous statuts</SelectItem>
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={enseigneFilter} onValueChange={setEnseigneFilter}>
                  <SelectTrigger className="w-full sm:w-[150px] h-9" data-testid="enseigne-filter"><SelectValue placeholder="Enseigne" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes enseignes</SelectItem>
                    {enseignes.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Select value={scoreFilter} onValueChange={setScoreFilter}>
                  <SelectTrigger className="w-full sm:w-[140px] h-9" data-testid="score-filter"><SelectValue placeholder="Score" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous scores</SelectItem>
                    <SelectItem value="conforme">Conforme</SelectItem>
                    <SelectItem value="vigilance">Vigilance</SelectItem>
                    <SelectItem value="bloquant">Bloquant</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={createurFilter} onValueChange={setCreateurFilter}>
                  <SelectTrigger className="w-full sm:w-[170px] h-9" data-testid="createur-filter"><SelectValue placeholder="Créateur" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous créateurs</SelectItem>
                    {createurs.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-full sm:w-[150px] h-9" placeholder="Date début" data-testid="date-from" />
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-full sm:w-[150px] h-9" placeholder="Date fin" data-testid="date-to" />
                {(statusFilter !== 'all' || enseigneFilter !== 'all' || scoreFilter !== 'all' || createurFilter !== 'all' || dateFrom || dateTo) && (
                  <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => { setStatusFilter('all'); setEnseigneFilter('all'); setScoreFilter('all'); setCreateurFilter('all'); setDateFrom(''); setDateTo(''); }}>
                    Réinitialiser
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Mobile card view + Desktop table view */}
        {/* Mobile cards (visible on small screens) */}
        <div className="block sm:hidden space-y-3">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500">Aucun prérequis trouvé</div>
          ) : filtered.map(item => {
            const sc = STATUS_CONFIG[item.status] || STATUS_CONFIG.brouillon;
            const Icon = sc.icon;
            return (
              <Card key={item.id} className="cursor-pointer active:scale-[0.98] transition-transform" onClick={() => navigate(`/prerequis-techniques/${item.id}`)}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{item.magasin || '-'}</p>
                      <p className="text-xs text-slate-500">{item.enseigne} — {item.code_postal} {item.ville}</p>
                    </div>
                    <ScoreIndicator score={item.score} />
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <Badge className={`${sc.color} text-xs`}><Icon className="w-3 h-3 mr-1" />{sc.label}</Badge>
                    <span className="text-xs text-slate-400">{formatDate(item.created_at)}</span>
                  </div>
                  {item.created_by_name && <p className="text-xs text-slate-500 mt-1">{item.created_by_name}</p>}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Desktop table (hidden on small screens) */}
        <Card className="hidden sm:block">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Magasin</TableHead>
                    <TableHead>Enseigne</TableHead>
                    <TableHead>Ville</TableHead>
                    <TableHead>Créateur</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-center">Score</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                        Aucun prérequis technique trouvé
                      </TableCell>
                    </TableRow>
                  ) : filtered.map(item => {
                    const sc = STATUS_CONFIG[item.status] || STATUS_CONFIG.brouillon;
                    const Icon = sc.icon;
                    return (
                      <TableRow key={item.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/prerequis-techniques/${item.id}`)} data-testid={`row-${item.id}`}>
                        <TableCell className="text-sm">{formatDate(item.created_at)}</TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-1.5">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            {item.magasin || '-'}
                          </div>
                        </TableCell>
                        <TableCell>{item.enseigne || '-'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5 text-slate-400" />
                            {item.code_postal} {item.ville}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{item.created_by_name || '-'}</TableCell>
                        <TableCell>
                          <Badge className={`${sc.color} text-xs`}><Icon className="w-3 h-3 mr-1" />{sc.label}</Badge>
                        </TableCell>
                        <TableCell className="text-center"><ScoreIndicator score={item.score} /></TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); navigate(`/prerequis-techniques/${item.id}`); }} data-testid={`view-${item.id}`}>
                            <Eye className="w-4 h-4 mr-1" /> Voir
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <p className="text-xs text-center text-slate-400">{filtered.length} résultat(s) sur {items.length}</p>
      </div>
    </AppLayout>
  );
}
